<script setup>
import { ref, computed, defineProps, watch } from 'vue';
import { IconSettings } from '@arco-design/web-vue/es/icon';
import { useFileSettingsStore } from '@/store/fileSettings'; // Import the store
import ZlButton from '@/components/ZlButton.vue';

const props = defineProps({
  year: {
    type: Number,
    required: true
  },
  month: {
    type: Number,
    required: true
  }
});

const fileSettingsStore = useFileSettingsStore(); // Instantiate the store

// --- Mock Data --- (Replace with actual API calls later)
const files = ref([]);

const fetchFiles = () => {
  console.log(`[PdfList] fetchFiles called for Year: ${props.year}, Month: ${props.month}`); // Added log
  // Mock file data based on selections - Replace with API call
  const type = 'PDF'; // This component is only for PDFs
  const year = props.year;
  const month = props.month;
  const count = 18; // Example: 18 PDFs to test pagination
  const extension = 'pdf';
  const stores = ['门店A', '门店B', '门店C'];
  const workOrders = ['WO-001', 'WO-002', 'WO-003', 'WO-004', 'WO-005'];

  files.value = Array.from({ length: count }, (_, i) => {
    const day = Math.floor(Math.random() * 28) + 1; // Random day
    const size = Math.floor(Math.random() * 5000) + 100; // Random size in KB
    const store = stores[i % stores.length];
    const workOrder = workOrders[i % workOrders.length];
    const date = new Date(year, month - 1, day);

    return {
      id: `${type}-${year}-${month}-${i + 1}`,
      name: `${type}${i + 1}.${extension}`,
      url: `/mock/${type}${i + 1}.${extension}`, // Placeholder URL
      generationTime: date.toLocaleDateString('zh-CN') + ' ' + date.toLocaleTimeString('zh-CN'),
      size: `${size} KB`,
      store: store,
      workOrder: workOrder,
    };
  });
  currentPage.value = 1; // Reset pagination on data change
};

// --- Pagination (Basic Example) ---
const currentPage = ref(1);
const itemsPerPage = ref(15); // Items per page for PDFs

// --- Table Columns --- (Now managed by Pinia)
const allColumns = [
  { title: '文件名', dataIndex: 'name', key: 'name', sortable: { sortDirections: ['ascend', 'descend'] } },
  { title: '生成时间', dataIndex: 'generationTime', key: 'generationTime', sortable: { sortDirections: ['ascend', 'descend'] } },
  { title: '大小', dataIndex: 'size', key: 'size', sortable: { sortDirections: ['ascend', 'descend'] } },
  { title: '所属门店', dataIndex: 'store', key: 'store', sortable: { sortDirections: ['ascend', 'descend'] } },
  { title: '关联工单', dataIndex: 'workOrder', key: 'workOrder', sortable: { sortDirections: ['ascend', 'descend'] } },
  { title: '操作', key: 'actions', slotName: 'actions', align: 'center' },
];

// Use Pinia state for initial visibility and make it reactive
const visibleColumns = ref(fileSettingsStore.pdfVisibleColumns);

// Function to update Pinia store when columns change
const handleColumnsChange = (newVisibleKeys) => {
  visibleColumns.value = newVisibleKeys;
  fileSettingsStore.setPdfVisibleColumns(newVisibleKeys);
};

// Computed property for the table columns based on visibility
const tableColumns = computed(() => {
  return allColumns.filter(col => visibleColumns.value.includes(col.key));
});

const paginatedFiles = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value;
  const end = start + itemsPerPage.value;
  return files.value.slice(start, end);
});

const totalPages = computed(() => {
  return Math.ceil(files.value.length / itemsPerPage.value);
});

function changePage(page) {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page;
  }
}

// Fetch files when props change
watch(() => [props.year, props.month], (newVal, oldVal) => {
  console.log(`[PdfList] Watch triggered. New: ${newVal}, Old: ${oldVal}`); // Added log
  fetchFiles();
}, { immediate: true });

</script>

<template>
  <div class="pdf-list-container">
    <div v-if="files.length > 0">
      <!-- Table Controls: Column Settings -->
      <div class="table-controls">
        <a-popover trigger="click" position="br">
          <template #content>
            <div class="column-selector">
              <a-checkbox-group
                direction="vertical"
                :model-value="visibleColumns"
                @change="handleColumnsChange"
              >
                <a-checkbox v-for="col in allColumns.filter(c => c.key !== 'actions')" :key="col.key" :value="col.key">
                  {{ col.title }}
                </a-checkbox>
              </a-checkbox-group>
            </div>
          </template>
          <ZlButton variant="glass" size="small" rounded>
            <template #icon><icon-settings /></template>
            列设置
          </ZlButton>
        </a-popover>
      </div>
      <a-table
        :columns="tableColumns" 
        :data="paginatedFiles"
        :pagination="false" 
        row-key="id"
        stripe
        hoverable
        :bordered="{cell:true}"
      >
        <template #columns>
          <a-table-column
            v-for="column in tableColumns"
            :key="column.key"
            :data-index="column.dataIndex"
            :title="column.title"
            :sortable="column.sortable"
            :align="column.align || 'left'"
          >
            <template v-if="column.key === 'actions'" #cell="{ record }">
              <ZlButton variant="info" size="small">查看</ZlButton>
              <ZlButton variant="danger" size="small">删除</ZlButton>
            </template>
          </a-table-column>
        </template>
      </a-table>

      <!-- Pagination Controls -->
      <div class="pagination">
        <a-pagination
          :total="files.length"
          :current="currentPage"
          :page-size="itemsPerPage"
          @change="changePage"
          show-total
          show-jumper
        />
      </div>
    </div>
    <div v-else class="no-files">
      <a-empty description="当前月份没有找到PDF文件" />
    </div>
  </div>
</template>

<style scoped>
.table-controls {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px; /* Add some space below controls */
}

.column-selector {
  padding: 10px;
  background-color: var(--color-bg-popup);
  border: 1px solid var(--color-border-2);
  border-radius: var(--border-radius-medium);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.column-selector .arco-checkbox-group {
  display: flex;
  flex-direction: column;
}

.column-selector .arco-checkbox {
  margin-bottom: 8px; /* Spacing between checkboxes */
}

.column-selector .arco-checkbox:last-child {
  margin-bottom: 0;
}

.pdf-list-container {
  /* Add container specific styles if needed */
}

.no-files {
    text-align: center;
    color: var(--color-text-3);
    margin-top: 40px;
    font-size: 1.1em;
}

/* PDF Table - remove old list styles */
:deep(.arco-table-th) {
  background-color: var(--color-fill-2);
  font-weight: bold;
  color: var(--color-text-1);
}

:deep(.arco-table-td) {
   color: var(--color-text-2);
}

:deep(.arco-table-tr:hover .arco-table-td) {
  background-color: var(--color-fill-1);
}

/* Pagination */
.pagination {
  display: flex;
  justify-content: right;
  gap: 5px;
  margin-top: 20px; /* Add margin above pagination */
  padding-top: 15px;
  border-top: 1px solid var(--color-border-1);
}
</style>